/**
 * TC-DSL Parser
 *
 * Converts TradeCraft DSL text into a Strategy tree for the Builder.
 * This is the inverse of generateDSL in dslService.ts.
 *
 * Handles all node types: asset, group, equal, weights, inv_vol, if/else, filter.
 */

import type {
  Strategy,
  StrategyNode,
  StrategyMetadata,
  SignalType,
  CompareOperator,
  Signal,
  Condition,
  FilterSignal,
  Selector,
  SelectorDirection,
} from '../types/builder';
import { generateNodeId, SIGNAL_DSL_NAMES } from '../types/builder';

// ─── Reverse lookup: DSL method name → SignalType ───────────

const DSL_TO_SIGNAL: Record<string, SignalType> = {};
for (const [key, val] of Object.entries(SIGNAL_DSL_NAMES)) {
  DSL_TO_SIGNAL[val] = key as SignalType;
}

// ─── Line-based parser state ────────────────────────────────

interface Line {
  indent: number;   // number of leading spaces
  text: string;     // trimmed content
  raw: string;      // original line
  lineNum: number;
}

function tokenize(dsl: string): Line[] {
  return dsl.split('\n').map((raw, i) => {
    const trimmed = raw.trimEnd();
    const indent = trimmed.length - trimmed.trimStart().length;
    return { indent, text: trimmed.trim(), raw: trimmed, lineNum: i + 1 };
  }).filter(l => l.text.length > 0);
}

// ─── Metadata Parsing ───────────────────────────────────────

function parseMetadata(lines: Line[]): { metadata: StrategyMetadata; rootStart: number } {
  const metadata: StrategyMetadata = {
    name: 'Untitled',
    rebalanceMode: 'daily',
  };

  let i = 0;

  // Line 0: strategy "Name":
  const nameMatch = lines[i]?.text.match(/^strategy\s+"([^"]+)":/);
  if (nameMatch) {
    metadata.name = nameMatch[1];
    i++;
  }

  // Parse header lines until we hit "root:"
  while (i < lines.length) {
    const text = lines[i].text;

    if (text === 'root:') {
      return { metadata, rootStart: i + 1 };
    }

    const rebalanceDaily = text.match(/^rebalance:\s*daily$/);
    if (rebalanceDaily) {
      metadata.rebalanceMode = 'daily';
      i++;
      continue;
    }

    const rebalanceThreshold = text.match(/^rebalance:\s*threshold\((\d+(?:\.\d+)?)\)$/);
    if (rebalanceThreshold) {
      metadata.rebalanceMode = 'threshold';
      metadata.rebalanceThreshold = parseFloat(rebalanceThreshold[1]);
      i++;
      continue;
    }

    const descMatch = text.match(/^description:\s*"([^"]*)"$/);
    if (descMatch) {
      metadata.description = descMatch[1];
      i++;
      continue;
    }

    // Skip asset_class and other unknown header lines
    i++;
  }

  return { metadata, rootStart: i };
}

// ─── Signal Parsing ─────────────────────────────────────────

function parseSignalExpr(expr: string): Signal {
  // TICKER.price
  const priceMatch = expr.match(/^([A-Z][A-Z0-9/]*)\.price$/);
  if (priceMatch) {
    return { type: 'current-price', ticker: priceMatch[1] };
  }

  // TICKER.method(window)
  const methodMatch = expr.match(/^([A-Z][A-Z0-9/]*)\.(\w+)\((\d+)\)$/);
  if (methodMatch) {
    const ticker = methodMatch[1];
    const method = methodMatch[2];
    const window = parseInt(methodMatch[3]);
    const signalType = DSL_TO_SIGNAL[method];
    if (!signalType) throw new Error(`Unknown signal method: ${method}`);
    return { type: signalType, ticker, window };
  }

  throw new Error(`Cannot parse signal expression: ${expr}`);
}

function parseFilterSignal(text: string): FilterSignal {
  // price
  if (text === 'price') {
    return { type: 'current-price' };
  }

  // method(window)
  const match = text.match(/^(\w+)\((\d+)\)$/);
  if (match) {
    const method = match[1];
    const window = parseInt(match[2]);
    const signalType = DSL_TO_SIGNAL[method];
    if (!signalType) throw new Error(`Unknown filter signal: ${method}`);
    return { type: signalType, window };
  }

  throw new Error(`Cannot parse filter signal: ${text}`);
}

function parseCondition(condText: string): Condition {
  // Split on operator: expr OP expr_or_number
  const opMatch = condText.match(/^(.+?)\s*(>=|<=|>|<)\s*(.+)$/);
  if (!opMatch) throw new Error(`Cannot parse condition: ${condText}`);

  const leftStr = opMatch[1].trim();
  const operator = opMatch[2] as CompareOperator;
  const rightStr = opMatch[3].trim();

  const left = parseSignalExpr(leftStr);

  // Right side: number or signal expression
  const numVal = parseFloat(rightStr);
  if (!isNaN(numVal) && /^-?\d+(\.\d+)?$/.test(rightStr)) {
    return { operator, left, right: numVal };
  }

  const right = parseSignalExpr(rightStr);
  return { operator, left, right };
}

// ─── Node Parsing ───────────────────────────────────────────

/**
 * Parse a node starting at lines[start], where all node content
 * is at indent >= baseIndent. Returns the parsed node and the
 * index of the next line after this node.
 */
function parseNode(lines: Line[], start: number, _baseIndent: number): { node: StrategyNode; next: number } {
  if (start >= lines.length) {
    return { node: { id: generateNodeId(), type: 'asset', ticker: 'SPY' }, next: start };
  }

  const line = lines[start];
  const text = line.text;

  // ─── equal: ────────────────────────────────────────
  if (text === 'equal:') {
    const children: StrategyNode[] = [];
    let i = start + 1;
    while (i < lines.length && lines[i].indent > line.indent) {
      if (lines[i].text === '-') {
        // Child follows on next line(s) at deeper indent
        const childIndent = lines[i].indent + 2; // expect child indented under the dash
        i++;
        if (i < lines.length && lines[i].indent >= childIndent) {
          const result = parseNode(lines, i, childIndent);
          children.push(result.node);
          i = result.next;
        }
      } else {
        i++;
      }
    }
    return {
      node: { id: generateNodeId(), type: 'weight-equal', children },
      next: i,
    };
  }

  // ─── weights: ──────────────────────────────────────
  if (text === 'weights:') {
    const children: StrategyNode[] = [];
    const weights: number[] = [];
    let i = start + 1;
    while (i < lines.length && lines[i].indent > line.indent) {
      const weightMatch = lines[i].text.match(/^-\s+(\d+)%:$/);
      if (weightMatch) {
        weights.push(parseInt(weightMatch[1]) / 100);
        const childIndent = lines[i].indent + 2;
        i++;
        if (i < lines.length && lines[i].indent >= childIndent) {
          const result = parseNode(lines, i, childIndent);
          children.push(result.node);
          i = result.next;
        }
      } else {
        i++;
      }
    }
    return {
      node: { id: generateNodeId(), type: 'weight-specified', weights, children },
      next: i,
    };
  }

  // ─── inv_vol(WINDOW): ─────────────────────────────
  const invVolMatch = text.match(/^inv_vol\((\d+)\):$/);
  if (invVolMatch) {
    const window = parseInt(invVolMatch[1]);
    const children: StrategyNode[] = [];
    let i = start + 1;
    while (i < lines.length && lines[i].indent > line.indent) {
      if (lines[i].text === '-') {
        const childIndent = lines[i].indent + 2;
        i++;
        if (i < lines.length && lines[i].indent >= childIndent) {
          const result = parseNode(lines, i, childIndent);
          children.push(result.node);
          i = result.next;
        }
      } else {
        i++;
      }
    }
    return {
      node: { id: generateNodeId(), type: 'weight-inverse-volatility', window, children },
      next: i,
    };
  }

  // ─── group "Name": ────────────────────────────────
  const groupMatch = text.match(/^group\s+"([^"]+)":$/);
  if (groupMatch) {
    const name = groupMatch[1];
    const childIndent = line.indent + 2;
    let i = start + 1;
    let child: StrategyNode = { id: generateNodeId(), type: 'asset', ticker: 'SPY' };
    if (i < lines.length && lines[i].indent >= childIndent) {
      const result = parseNode(lines, i, childIndent);
      child = result.node;
      i = result.next;
    }
    return {
      node: { id: generateNodeId(), type: 'group', name, child },
      next: i,
    };
  }

  // ─── if COND: ... else: ... ───────────────────────
  const ifMatch = text.match(/^if\s+(.+):$/);
  if (ifMatch) {
    const condition = parseCondition(ifMatch[1]);
    const thenIndent = line.indent + 2;
    let i = start + 1;

    // Parse then branch
    let thenBranch: StrategyNode = { id: generateNodeId(), type: 'asset', ticker: 'SPY' };
    if (i < lines.length && lines[i].indent >= thenIndent) {
      const result = parseNode(lines, i, thenIndent);
      thenBranch = result.node;
      i = result.next;
    }

    // Find and parse else branch
    let elseBranch: StrategyNode = { id: generateNodeId(), type: 'asset', ticker: 'SPY' };
    if (i < lines.length && lines[i].text === 'else:' && lines[i].indent === line.indent) {
      const elseIndent = lines[i].indent + 2;
      i++;
      if (i < lines.length && lines[i].indent >= elseIndent) {
        const result = parseNode(lines, i, elseIndent);
        elseBranch = result.node;
        i = result.next;
      }
    }

    return {
      node: { id: generateNodeId(), type: 'if', condition, thenBranch, elseBranch },
      next: i,
    };
  }

  // ─── filter top(N)/bottom(N) by signal: ───────────
  const filterMatch = text.match(/^filter\s+(top|bottom)\((\d+)\)\s+by\s+(.+):$/);
  if (filterMatch) {
    const direction = filterMatch[1] as SelectorDirection;
    const count = parseInt(filterMatch[2]);
    const signal = parseFilterSignal(filterMatch[3]);
    const selector: Selector = { direction, count };

    const children: StrategyNode[] = [];
    let i = start + 1;
    while (i < lines.length && lines[i].indent > line.indent) {
      if (lines[i].text === '-') {
        const childIndent = lines[i].indent + 2;
        i++;
        if (i < lines.length && lines[i].indent >= childIndent) {
          const result = parseNode(lines, i, childIndent);
          children.push(result.node);
          i = result.next;
        }
      } else {
        i++;
      }
    }

    return {
      node: { id: generateNodeId(), type: 'filter', signal, selector, children },
      next: i,
    };
  }

  // ─── TICKER or TICKER as "Name" ───────────────────
  const assetAliasMatch = text.match(/^([A-Z][A-Z0-9/]*)\s+as\s+"([^"]+)"$/);
  if (assetAliasMatch) {
    return {
      node: { id: generateNodeId(), type: 'asset', ticker: assetAliasMatch[1], name: assetAliasMatch[2] },
      next: start + 1,
    };
  }

  const tickerMatch = text.match(/^([A-Z][A-Z0-9/]*)$/);
  if (tickerMatch) {
    return {
      node: { id: generateNodeId(), type: 'asset', ticker: tickerMatch[1] },
      next: start + 1,
    };
  }

  // Unknown line — skip it
  return {
    node: { id: generateNodeId(), type: 'asset', ticker: 'SPY' },
    next: start + 1,
  };
}

// ─── Public API ─────────────────────────────────────────────

/**
 * Parse a TC-DSL string into a Strategy tree for the Builder.
 * Throws on invalid input.
 */
export function parseDSL(dsl: string): Strategy {
  const lines = tokenize(dsl);
  if (lines.length === 0) throw new Error('Empty strategy code');

  const { metadata, rootStart } = parseMetadata(lines);

  if (rootStart >= lines.length) {
    throw new Error('No root node found in strategy');
  }

  const { node } = parseNode(lines, rootStart, lines[rootStart].indent);

  return { metadata, root: node };
}
